import type { StaticCollider } from '../sim/sim';
import type { SurfaceId } from '../sim/types';
import type { BuiltStage, MeshData, PadStageSpec } from './types';

/** Builds the hidden flat test pad (physics tests, M1 skeleton): zones, ramps, slopes, trees, rocks. */
export function buildPad(spec: PadStageSpec): BuiltStage {
  const half = spec.size / 2;
  const colliders: StaticCollider[] = [];
  const N = 64;
  colliders.push({ kind: 'heightfield', nrows: N, ncols: N, heights: new Float32Array((N + 1) * (N + 1)), scale: [spec.size, 1, spec.size], center: [0, 0, 0] });

  const terrain: MeshData[] = [];
  terrain.push(gridMesh(-half, -half, spec.size, spec.size, 64, () => 0));

  const road: MeshData[] = [];
  // Slopes: inclined rectangles rising along +z.
  for (const sl of spec.slopes) {
    const [x0, z0, x1, z1] = sl.rect;
    const m = gridMesh(x0, z0, x1 - x0, z1 - z0, Math.ceil((x1 - x0) / 1), (_x, z) => Math.max(0, (z - z0) * sl.grade) + 0.002);
    road.push(m);
    colliders.push({ kind: 'trimesh', vertices: m.positions, indices: m.indices });
  }
  // Ramps (kicker wedges).
  for (const r of spec.ramps) {
    const m = rampMesh(r.pos[0], r.pos[1], r.yaw, r.length, r.width, r.height);
    road.push(m);
    colliders.push({ kind: 'trimesh', vertices: m.positions, indices: m.indices });
  }
  for (const [x, z] of spec.trees) colliders.push({ kind: 'cylinder', pos: [x, 4, z], halfHeight: 4, radius: 0.22, mat: 'wood' });
  for (const [x, z, r] of spec.rocks) colliders.push({ kind: 'ball', pos: [x, r * 0.35, z], radius: r, mat: 'rock' });

  const heightAt = (x: number, z: number): number => {
    for (const sl of spec.slopes) {
      const [x0, z0, x1, z1] = sl.rect;
      if (x >= x0 && x <= x1 && z >= z0 && z <= z1) return (z - z0) * sl.grade;
    }
    return 0;
  };

  const surfaceAt = (x: number, z: number): SurfaceId => {
    for (const sl of spec.slopes) {
      const [x0, z0, x1, z1] = sl.rect;
      if (x >= x0 && x <= x1 && z >= z0 && z <= z1) return sl.surface;
    }
    for (let i = spec.zones.length - 1; i >= 0; i--) {
      const zn = spec.zones[i];
      const [x0, z0, x1, z1] = zn.rect;
      if (x >= x0 && x <= x1 && z >= z0 && z <= z1) return zn.surface;
    }
    return spec.baseSurface;
  };

  const treeData = new Float32Array(spec.trees.length * 5);
  spec.trees.forEach(([x, z], i) => treeData.set([x, 0, z, (i * 2.399) % (Math.PI * 2), 0.9 + ((i * 7) % 5) * 0.06], i * 5));
  const rockData = new Float32Array(spec.rocks.length * 5);
  spec.rocks.forEach(([x, z, r], i) => rockData.set([x, r * 0.35 - r, z, i * 1.3, r], i * 5));

  return {
    spec,
    colliders,
    surfaces: { surfaceAt },
    heightAt,
    track: null,
    spawn: { x: spec.spawn.x, y: heightAt(spec.spawn.x, spec.spawn.z) + 0.05, z: spec.spawn.z, heading: spec.spawn.heading },
    terrain,
    road,
    instances: [
      { kind: 'tree', data: treeData },
      { kind: 'rock', data: rockData },
    ],
    bounds: { minX: -half, maxX: half, minZ: -half, maxZ: half },
  };
}

/** Regular grid mesh over [x0, x0+w] × [z0, z0+d] with height function h. */
export function gridMesh(x0: number, z0: number, w: number, d: number, n: number, h: (x: number, z: number) => number): MeshData {
  const nx = Math.max(1, n);
  const nz = Math.max(1, Math.round((n * d) / w));
  const verts = (nx + 1) * (nz + 1);
  const positions = new Float32Array(verts * 3);
  const normals = new Float32Array(verts * 3);
  const uvs = new Float32Array(verts * 2);
  for (let j = 0; j <= nz; j++) {
    for (let i = 0; i <= nx; i++) {
      const x = x0 + (w * i) / nx;
      const z = z0 + (d * j) / nz;
      const k = j * (nx + 1) + i;
      positions[k * 3] = x;
      positions[k * 3 + 1] = h(x, z);
      positions[k * 3 + 2] = z;
      uvs[k * 2] = x / 4;
      uvs[k * 2 + 1] = z / 4;
    }
  }
  const indices = new Uint32Array(nx * nz * 6);
  let p = 0;
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const a = j * (nx + 1) + i;
      const b = a + 1;
      const c = a + nx + 1;
      const e = c + 1;
      indices[p++] = a;
      indices[p++] = c;
      indices[p++] = b;
      indices[p++] = b;
      indices[p++] = c;
      indices[p++] = e;
    }
  }
  computeNormals(positions, indices, normals);
  return { positions, normals, uvs, indices };
}

export function computeNormals(pos: Float32Array, idx: Uint32Array, out: Float32Array): void {
  out.fill(0);
  for (let t = 0; t < idx.length; t += 3) {
    const a = idx[t] * 3;
    const b = idx[t + 1] * 3;
    const c = idx[t + 2] * 3;
    const ux = pos[b] - pos[a];
    const uy = pos[b + 1] - pos[a + 1];
    const uz = pos[b + 2] - pos[a + 2];
    const vx = pos[c] - pos[a];
    const vy = pos[c + 1] - pos[a + 1];
    const vz = pos[c + 2] - pos[a + 2];
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    for (const k of [a, b, c]) {
      out[k] += nx;
      out[k + 1] += ny;
      out[k + 2] += nz;
    }
  }
  for (let i = 0; i < out.length; i += 3) {
    const l = Math.hypot(out[i], out[i + 1], out[i + 2]) || 1;
    // Faces are wound so that normals point up for CCW-from-above; flip if needed.
    const s = out[i + 1] < 0 ? -1 : 1;
    out[i] = (out[i] / l) * s;
    out[i + 1] = (out[i + 1] / l) * s;
    out[i + 2] = (out[i + 2] / l) * s;
  }
}

function rampMesh(cx: number, cz: number, yaw: number, length: number, width: number, height: number): MeshData {
  // Local: x across, z along; base at z=-L/2 (h=0) rising to z=+L/2 (h=height), vertical back face.
  const hw = width / 2;
  const hl = length / 2;
  const local: [number, number, number][] = [
    [-hw, 0, -hl],
    [hw, 0, -hl],
    [-hw, height, hl],
    [hw, height, hl],
    [-hw, 0, hl],
    [hw, 0, hl],
  ];
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  const positions = new Float32Array(local.length * 3);
  local.forEach(([x, y, z], i) => {
    positions[i * 3] = cx + x * c + z * s;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = cz - x * s + z * c;
  });
  const indices = new Uint32Array([0, 2, 1, 1, 2, 3, 2, 4, 3, 3, 4, 5, 0, 4, 2, 1, 3, 5]);
  const normals = new Float32Array(positions.length);
  computeNormals(positions, indices, normals);
  const uvs = new Float32Array((positions.length / 3) * 2);
  return { positions, normals, uvs, indices };
}
